import { Box, LoadingOverlay } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { accountApi } from '@/api/accountApi'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { AuthLayout } from '@/components/layout/AuthLayout/AuthLayout'
import { RouterPath } from '@/router/paths'
import { useMfaLogin } from '@/session/useMfaLogin'
import { showToast } from '@/theme/notifications'
import { OtpHandler } from '../login/OtpForm/OtpHandler'
import {
  FormValues,
  SetNewPasswordForm
} from './SetNewPasswordForm/SetNewPasswordForm'

type SetNewPasswordHandlerProps = {
  uid: string
  email: string
  token: string
}

export const SetNewPasswordHandler = ({
  email,
  token,
  uid
}: SetNewPasswordHandlerProps) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { login, state, isPending: isLoginPending } = useMfaLogin()

  const { mutate, isPending: isResetPasswordPending } = useMutation({
    mutationFn: accountApi.resetPasswordConfirm,
    onSuccess: (_, variables) => {
      const { password } = variables

      login(
        { email, password },
        {
          onSuccess: () => {
            showToast(
              intl.formatMessage({ id: 'passwordReset.passwordChanged' }),
              'success'
            )
          },
          onError: () => {
            void navigate(RouterPath.login)
          }
        }
      )
    },
    onError: (err) => {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'passwordReset.passwordChangeError' })
        ),
        'error'
      )
    }
  })

  const isPending = isLoginPending || isResetPasswordPending

  const handleSubmit = (formValues: FormValues) => {
    if (isPending) {
      return
    }

    const { password } = formValues

    mutate({
      password,
      token,
      uid
    })
  }

  if (state?.isMfaRequired) {
    return (
      <AuthLayout variant="large">
        <OtpHandler
          uid={state.uid}
          email={state.email}
          resendKey={state.resendKey}
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />
        <SetNewPasswordForm onSubmit={handleSubmit} />
      </Box>
    </AuthLayout>
  )
}
